import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components/macro";
import {
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Grid,
  TextField as MuiTextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider as MuiDivider,
} from "@material-ui/core";
import { Alert as MuiAlert } from "@material-ui/lab";
import { spacing } from "@material-ui/system";
import { validEmailRegex } from "../../utils/AuthValidation";
import { changeEmail } from "../../models/actions/authActions";
import { useDispatch } from "react-redux";
import { decodeRx } from "../dashboards/Projects/utils";
import { ADMIN_EMAILS } from "../auth/SignInHidden";

const Alert = styled(MuiAlert)(spacing);
const Card = styled(MuiCard)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);

function AdminChangeEmail() {
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const [targetUserEmail, setTargetUserEmail] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [changeEmailError, setChangeEmailError] = useState("");
  const [changeEmailSuccess, setChangeEmailSuccess] = useState(false);
  // 관리자 이메일과 hidden 사용자 여부를 lazy initialization으로 저장 (고정값으로 유지)
  const [adminEmail, setAdminEmail] = useState(() => {
    const currentEmail = localStorage.getItem("0xaccountId");
    return currentEmail ? currentEmail.trim().toLowerCase() : "";
  });
  
  const [hasHiddenUser, setHasHiddenUser] = useState(() => {
    return localStorage.getItem("0xXRurEm") !== null;
  });

  // 렌더링 시 관리자 여부 확인 (매 렌더링마다 확인)
  const currentEmail = localStorage.getItem("0xaccountId");
  const isAdmin = currentEmail && ADMIN_EMAILS.includes(currentEmail.toLowerCase());
  
  // 관리자가 아니거나 hidden으로 들어오지 않은 경우 아무것도 표시하지 않음
  if (!isAdmin || !hasHiddenUser) {
    return null;
  }

  const handleOpenDialog = () => {
    // hidden으로 로그인한 User 이메일을 localStorage에서 가져오기
    const storedUserEmail = decodeRx(localStorage.getItem("0xXRurEm"));
    const targetEmail = storedUserEmail ? storedUserEmail.trim().toLowerCase() : "";
    
    setTargetUserEmail(targetEmail);
    setNewUserEmail("");
    setChangeEmailError("");
    setChangeEmailSuccess(false);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setTargetUserEmail("");
    setNewUserEmail("");
    setChangeEmailError("");
    setChangeEmailSuccess(false);
  };

  const handleChangeUserEmail = async () => {
    if (!targetUserEmail || !newUserEmail) {
      setChangeEmailError("Both email fields are required.");
      return;
    }

    if (!validEmailRegex.test(targetUserEmail)) {
      setChangeEmailError("Please enter a valid target email address.");
      return;
    }

    if (!validEmailRegex.test(newUserEmail)) {
      setChangeEmailError("Please enter a valid new email address.");
      return;
    }

    if (targetUserEmail.trim().toLowerCase() === newUserEmail.trim().toLowerCase()) {
      setChangeEmailError("New email must be different from the current email.");
      return;
    }

    setChangingEmail(true);
    setChangeEmailError("");
    setChangeEmailSuccess(false);

    try {
      // requesterEmail은 관리자 이메일 (고정값)
      await dispatch(changeEmail(
        targetUserEmail.trim().toLowerCase(),
        newUserEmail.trim().toLowerCase(),
        adminEmail // 관리자 이메일 사용
      ));
      setChangeEmailSuccess(true);
      setTimeout(() => {
        handleCloseDialog();
      }, 2000);
    } catch (error) {
      console.log("handleChangeUserEmail error:", error);
      let errorMessage = "Failed to change email. Please try again.";
      
      if (error && error.message) {
        errorMessage = error.message;
      } else if (error && typeof error === 'string') {
        errorMessage = error;
      } else if (error && error.status === 410) {
        errorMessage = error.message || "Company not found. Please check the email address.";
      } else if (error && error.status === 403) {
        errorMessage = "Forbidden: You don't have permission to change this email. Please check if you have admin privileges.";
      } else if (error && error.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
      } else if (error && error.status) {
        errorMessage = error.message || `Error ${error.status}: ${error.statusText || "Request failed"}`;
      } else if (error && error.resultcode) {
        errorMessage = error.message || `Error ${error.resultcode}`;
      }
      
      setChangeEmailError(errorMessage);
      setChangeEmailSuccess(false);
    } finally {
      setChangingEmail(false);
    }
  };

  return (
    <>
      <Card mb={6} style={{ minHeight: "200px" }}>
        <CardContent>
          <Grid container spacing={6}>
            <Grid item md={12}>
              <Typography variant="h6" gutterBottom>
                Admin: Change User Email
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: "20px" }}>
                Change a user's email address. This will update the owner of all existing data (projects, clients, reports, etc.) to the new email address.
              </Typography>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleOpenDialog}
                style={{
                  background: "#3DB5E6",
                  color: "#FFFFFF",
                }}
                classes={{
                  root: "button",
                  label: "button-label",
                }}
              >
                Change User Email
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Change Email Dialog */}
      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change User Email (Admin)</DialogTitle>
        <DialogContent>
          {changeEmailSuccess ? (
            <Alert mt={2} mb={1} severity="success">
              Email successfully changed! The dialog will close automatically.
            </Alert>
          ) : (
            <>
              {changeEmailError && (
                <Alert mt={2} mb={1} severity="error">
                  {changeEmailError}
                </Alert>
              )}
              <TextField
                type="email"
                label="Target User Email"
                value={targetUserEmail}
                onChange={(e) => setTargetUserEmail(e.target.value)}
                fullWidth
                margin="normal"
                helperText="Enter the current email address of the user"
                disabled={changingEmail}
              />
              <TextField
                type="email"
                label="New Email Address"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                fullWidth
                margin="normal"
                helperText="Enter the new email address. This will update the owner of all existing data."
                disabled={changingEmail}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={changingEmail}
          >
            {changeEmailSuccess ? "Close" : "Cancel"}
          </Button>
          {!changeEmailSuccess && (
            <Button
              onClick={handleChangeUserEmail}
              variant="contained"
              color="primary"
              disabled={changingEmail || !targetUserEmail || !newUserEmail}
            >
              {changingEmail ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 10 }} />
                  Changing...
                </>
              ) : (
                "Change Email"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AdminChangeEmail;

